import moment, { Moment } from 'moment';
class PatientID {
    private static _instance: PatientID = new PatientID();
    private static readonly MAX_PIN_LENGTH = 11;
    private static readonly PIN_SEPARATOR_INDEX = 8;
    private static readonly PIN_YEAR_POS = 6;

    constructor() {
        if (PatientID._instance) {
            throw new Error('Singleton class');
        }
        PatientID._instance = this;
    }

    public static get instance(): PatientID {
        return PatientID._instance;
    }

    getPinGreaterThan11Chars(dashIndex: number, pinLength: number, pin: string): string {
        const MAX_PIN_LENGTH = 13;
        //if there is already - then remove it and add new on index 8
        if (dashIndex !== -1 && dashIndex !== PatientID.PIN_SEPARATOR_INDEX) {
            const position = dashIndex;
            pin = pin.substring(0, position) + pin.substring(position + 1, pinLength);
        }
        if (dashIndex === -1) {
            //add - at index 8
            pin = `${pin.slice(0, PatientID.PIN_SEPARATOR_INDEX)}-${pin.slice(PatientID.PIN_SEPARATOR_INDEX)}`;
        }
        //trim if length is greater then 12
        if (pinLength > MAX_PIN_LENGTH) {
            pin = pin.substring(0, MAX_PIN_LENGTH);
        }
        return pin;
    }

    getPinEqualTo11Chars(dashIndex: number, pinLength: number, pin: string): string {
        //if there is already - then remove it and add new on index 6
        if (dashIndex !== -1 && dashIndex === PatientID.PIN_SEPARATOR_INDEX) {
            const position = dashIndex;
            pin = pin.substring(0, position) + pin.substring(position + 1, pinLength);
        }
        if (dashIndex === -1) {
            //add - at index 6
            pin = `${pin.slice(0, PatientID.PIN_YEAR_POS)}-${pin.slice(PatientID.PIN_YEAR_POS)}`;
        }
        return pin;
    }

    addDashToNumbers(pin: string): string {
        const dashIndex = pin.indexOf('-');
        const pinLength = pin.length;
        //if there is no dash, add a dash
        if (dashIndex === -1) {
            const PIN_YEAR_POS = 6;
            if (pinLength <= PatientID.MAX_PIN_LENGTH - 1 && pinLength > PIN_YEAR_POS) {
                return `${pin.slice(0, PIN_YEAR_POS)}-${pin.slice(PIN_YEAR_POS)}`;
            }
            if (pinLength > PatientID.MAX_PIN_LENGTH - 1) {
                const PIN_SEPARATOR_INDEX = 8;
                //add - at index 8
                return `${pin.slice(0, PIN_SEPARATOR_INDEX)}-${pin.slice(PIN_SEPARATOR_INDEX)}`;
            }
            return pin;
        }

        if (pinLength > PatientID.MAX_PIN_LENGTH) {
            return this.getPinGreaterThan11Chars(dashIndex, pinLength, pin);
        }
        if (pinLength === PatientID.MAX_PIN_LENGTH) {
            return this.getPinEqualTo11Chars(dashIndex, pinLength, pin);
        }
        return pin;
    }

    getPersonalNumber(input: string): { birthDate: Moment; birthNumber: string } {
        let birthDate: Moment;
        let birthDateStr: string;
        let birthNumberStr: string;

        if (input.indexOf('-') > -1) {
            // XXXXXXXX-NNNN, XXXXXX-NNNN
            [birthDateStr, birthNumberStr] = input.split('-');
            birthDate = moment(birthDateStr, birthDateStr.length === 8 ? 'YYYYMMDD' : 'YYMMDD');
        } else if (input.length === 10) {
            // XXXXXXNNNN
            birthDateStr = input.slice(0, 6);
            birthDate = moment(birthDateStr, 'YYMMDD');
            birthNumberStr = input.slice(6);
        } else if (input.length === 12) {
            // XXXXXXXXNNNN
            birthDateStr = input.slice(0, 8);
            birthDate = moment(birthDateStr, 'YYYYMMDD');
            birthNumberStr = input.slice(8);
        } else {
            throw new Error('Unsupported date format!');
        }

        return { birthDate, birthNumber: birthNumberStr };
    }

    isBirthNumberValid(birthDate: string, birthNumber: string, allowCoordinationNumber = true): boolean {
        if (isNaN(Number(birthNumber))) {
            // If birthNumber is not parsable as a number, return valid only if 4 capital letters. Otherwise invalid.
            return allowCoordinationNumber && /^[A-Z]{4}$/.test(birthNumber);
        } else if (!/^[0-9]{4}$/.test(birthNumber)) {
            // If birthNumber is parsable as a number, make sure it is between 0000 to 9999
            return false;
        }

        const numbers = (birthDate + birthNumber)
            .split('')
            .map((s) => Number(s))
            .reverse();

        const checksum = numbers.reduce((result, n, i) => {
            const isEven = (i + 1) % 2 === 0;
            const sum = isEven ? n * 2 : n;
            return result + (sum > 9 ? sum - 9 : sum);
        }, 0);

        return checksum % 10 === 0;
    }

    validatePersonalNumber(input: string, allowCoordinationNumber = true): boolean {
        if (!input) {
            return false;
        }

        try {
            const { birthDate, birthNumber } = this.getPersonalNumber(input);
            return (
                birthDate.isValid() &&
                this.isBirthNumberValid(birthDate.format('YYMMDD'), birthNumber, allowCoordinationNumber)
            );
        } catch (error) {
            return false;
        }
    }

    getInput(input: string, startingWith: string): string {
        return !input.startsWith(startingWith) ? startingWith + input : input;
    }

    checkForInputLength(input: string) {
        const inputLen = input.length;

        const YEAR_SHORT_LENGTH = 2;
        return inputLen >= YEAR_SHORT_LENGTH;
    }

    //method for added prefix of 19 and 20
    //currently the functionality is disable by comment
    validate4DigitInputYear(pin: string, pid: string) {
        let isPinValid = false;
        let valid19, valid20;
        //const input = pin;
        //const lenCheck = this.checkForInputLength(input);
        /*if (!input.startsWith('19') && lenCheck) {
            valid19 = this.validatePersonalNumber(this.getInput(input, '19'));
        }
        if (!input.startsWith('20') && lenCheck) {
            valid20 = this.validatePersonalNumber(this.getInput(input, '20'));
        }*/
        if (valid19) {
            pid = this.addDashToNumbers('19' + pin);
            // Recall service to check again the pin is valid or not, after filter
            isPinValid = this.validatePersonalNumber(pid);
        } else if (valid20) {
            pid = this.addDashToNumbers('20' + pin);
            // Recall service to check again the pin is valid or not, after filter
            isPinValid = this.validatePersonalNumber(pid);
        } else {
            isPinValid = false;
        }

        return {
            personalIdNum: pid,
            isPinValid,
        };
    }

    checkYearPrefix(pin: string, pid: string) {
        if (!pin) {
            return {
                isPinValid: false,
                pid,
            };
        }
        if (pin.match(/[a-z]/i)) {
            pin = pin.toUpperCase();
        }
        const pinLen = pin.length;

        if (pinLen >= PatientID.MAX_PIN_LENGTH + 1 && (pin.startsWith('19') || pin.startsWith('20'))) {
            pid = this.addDashToNumbers(pin);

            // Recall service to check again the pin is valid or not, after filter
            return {
                isPinValid: this.validatePersonalNumber(pid),
                pid,
            };
        }
        const { isPinValid, personalIdNum } = this.validate4DigitInputYear(pin, pid);
        return {
            isPinValid,
            pid: personalIdNum,
        };
    }
}

export const patientId = PatientID.instance;
